import { Link } from 'react-router-dom';
import SEOPageLayout, { tokens } from '../../components/SEOPageLayout';
import { appLinks } from '../../lib/crossSiteLinks';

const sampleExceptions = [
  {
    id: 'exc-001',
    exceptionType: 'merged_without_approval',
    severity: 'high',
    timestamp: '2026-01-02T02:15:44Z',
    repository: 'acme-corp/api-service',
    pullRequest: {
      number: 842,
      title: 'URGENT: Fix authentication bypass vulnerability',
      author: 'security.engineer',
      mergedBy: 'security.engineer',
      url: 'https://github.com/acme-corp/api-service/pull/842',
    },
    controlExpected: 'Minimum 1 approval required before merge',
    controlActual: 'Merged with 0 approvals',
    acknowledged: true,
    acknowledgedBy: 'cto@acme.com',
    acknowledgedAt: '2026-01-02T08:30:00Z',
    reason: 'Emergency security patch for critical vulnerability. Verbal approval obtained from CTO at 2:10 AM via phone. Post-merge review completed by senior.engineer at 8:45 AM.',
    status: 'resolved',
  },
  {
    id: 'exc-002',
    exceptionType: 'ci_checks_bypassed',
    severity: 'medium',
    timestamp: '2026-01-03T16:42:11Z',
    repository: 'acme-corp/frontend-app',
    pullRequest: {
      number: 1205,
      title: 'Update copy for marketing campaign',
      author: 'content.manager',
      mergedBy: 'lead.engineer',
      url: 'https://github.com/acme-corp/frontend-app/pull/1205',
    },
    controlExpected: 'All CI checks must pass before merge',
    controlActual: 'Merged with 1 failing check: visual-regression-tests',
    acknowledged: true,
    acknowledgedBy: 'lead.engineer@acme.com',
    acknowledgedAt: '2026-01-03T16:50:00Z',
    reason: 'Visual regression test failed due to intentional copy change. Screenshots updated in follow-up commit #a8f2c1d.',
    status: 'resolved',
  },
  {
    id: 'exc-003',
    exceptionType: 'direct_push',
    severity: 'critical',
    timestamp: '2026-01-04T11:23:55Z',
    repository: 'acme-corp/infrastructure',
    branch: 'main',
    commit: {
      sha: 'b7c8d9e0f1...',
      message: 'Fix SSL certificate path',
      author: 'devops.engineer',
    },
    controlExpected: 'All changes to main branch must go through pull request',
    controlActual: 'Direct push to main branch',
    acknowledged: false,
    status: 'open',
  },
];

export default function SampleExceptionPage() {
  return (
    <SEOPageLayout
      title="Sample Exception Records - Documented Control Bypasses"
      description="See examples of EvidentTrail exception tracking for control bypasses. Learn how to document emergency changes and CI bypasses for auditors."
    >
      {/* Hero */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h1 className={`${tokens.heading.h1} mb-6`}>
              Sample Exception Records
            </h1>
            <p className={tokens.text.large}>
              These examples show how EvidentTrail documents control bypasses and 
              exceptions. All data is anonymized and for illustration purposes only.
            </p>
          </div>
        </div>
      </section>

      {/* Exceptions */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className={`${tokens.heading.h2} mb-6`}>Exception Records</h2>
            
            {sampleExceptions.map((exception) => (
              <div 
                key={exception.id}
                className={`${tokens.card} ${
                  exception.status === 'open' 
                    ? 'border-red-500/30' 
                    : 'border-green-500/30'
                }`}
              >
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    exception.severity === 'critical' 
                      ? 'bg-red-500/20 text-red-400'
                      : exception.severity === 'high'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {exception.severity}
                  </span>
                  <span className="px-2 py-1 bg-slate-700 rounded text-xs font-mono text-slate-300">
                    {exception.exceptionType}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    exception.status === 'open' 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-green-500/20 text-green-400'
                  }`}>
                    {exception.status}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div>
                    <span className="text-slate-500 text-sm">Repository:</span>
                    <span className="ml-2 text-slate-300">{exception.repository}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-sm">Timestamp:</span>
                    <span className="ml-2 text-slate-300">
                      {new Date(exception.timestamp).toLocaleString()}
                    </span>
                  </div>
                  {exception.pullRequest && (
                    <div>
                      <span className="text-slate-500 text-sm">Pull Request:</span>
                      <span className="ml-2 text-slate-300">
                        #{exception.pullRequest.number} — {exception.pullRequest.title}
                      </span>
                    </div>
                  )}
                  {exception.commit && (
                    <div>
                      <span className="text-slate-500 text-sm">Commit:</span>
                      <span className="ml-2 text-slate-300 font-mono text-sm">
                        {exception.commit.sha} — {exception.commit.message}
                      </span>
                    </div>
                  )}
                </div>

                <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Expected Control:</span>
                      <p className="text-slate-300 mt-1">{exception.controlExpected}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Actual Behavior:</span>
                      <p className="text-red-400 mt-1">{exception.controlActual}</p>
                    </div>
                  </div>
                </div>

                {exception.acknowledged ? (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-400 font-medium">Acknowledged</span>
                      <span className="text-slate-500 text-sm">
                        by {exception.acknowledgedBy} on {new Date(exception.acknowledgedAt!).toLocaleDateString()}
                      </span>
                    </div>
                    <p className={tokens.text.base}>{exception.reason}</p>
                  </div>
                ) : (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-red-400 font-medium">Pending Acknowledgment</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-2">
                      This exception requires review and documentation before the next audit.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Exception Documentation Best Practices</h2>
            <div className="space-y-4">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-2`}>Acknowledge Promptly</h3>
                <p className={tokens.text.base}>
                  Document exceptions as soon as possible while context is fresh. 
                  "I don't remember" is not an acceptable auditor response.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-2`}>Be Specific</h3>
                <p className={tokens.text.base}>
                  Include who approved the exception, when, and through what channel 
                  (phone, Slack, in-person). Reference ticket numbers if applicable.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-2`}>Follow Up</h3>
                <p className={tokens.text.base}>
                  If an emergency change was made, document any post-merge review 
                  that was performed to verify the change.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Samples */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Related Sample Outputs</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/sample-timeline" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h3 className={`${tokens.heading.h3} mb-2`}>Sample Audit Timeline</h3>
                <p className={tokens.text.base}>Trace approvals, checks, and deployments end to end.</p>
              </Link>
              <Link to="/sample-drift-event" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h3 className={`${tokens.heading.h3} mb-2`}>Sample Drift Event</h3>
                <p className={tokens.text.base}>Review a drift change with severity and timestamps.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Start Tracking Exceptions</h2>
            <p className={`${tokens.text.base} mb-8`}>
              Automatically detect and document control bypasses in your repositories.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/sample-timeline" className="text-slate-300 hover:text-white transition-colors">
                See Sample Timeline →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
